import { Row } from '@tanstack/react-table';
import { InfoIcon } from 'lucide-react';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { formatTrimmedDecimal } from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { SalaryAdjustmentLineLabel, SalaryAdjustmentLineTypes } from '@/modules/finance/common';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import {
  useIsSalaryOnlyBill,
  useSalaryAdjustmentValue,
} from '../../../hooks/generate-bills/use-salary-adjustment-cells';
import { GenerateBillListItem, GenerateBillSalaryAdjustmentListItem } from '../../../types';

/** Muted styling for a salary-adjustment row that nets to zero and won't be billed. */
const EMPTY_ADJUSTMENT_ROW_CLASS = 'text-muted-foreground';

interface SalaryCellProps {
  row: Row<GenerateBillListItem>;
}

/**
 * Shown next to a member's name when salary mode is on but nothing is selected beneath it, so
 * the bill will contain the salary line alone. Without this the user has no way to tell that an
 * apparently "empty" selection still produces a valid, complete bill.
 */
export const SalaryOnlyBillIndicator = ({ row }: SalaryCellProps) => {
  const isSalaryOnly = useIsSalaryOnlyBill(row);

  if (!isSalaryOnly) return null;

  return (
    <SimpleTooltip
      content={i18n.t('authenticated.bills:monthlySalary.salaryOnlyBillTooltip')}
      contentClassName="max-w-xs"
    >
      <InfoIcon size={16} className="shrink-0 cursor-help text-muted-foreground" />
    </SimpleTooltip>
  );
};

/**
 * Name cell for the derived salary line. Reads "Monthly salary" when it is the bill's only line
 * (there is nothing to reconcile against) and "Monthly salary adjustment" otherwise.
 */
export const SalaryAdjustmentLabel = ({ row }: SalaryCellProps) => {
  const parentRow = row.getParentRow();
  const isSalaryOnly = useIsSalaryOnlyBill(parentRow ?? row);
  const { isEmpty } = useSalaryAdjustmentValue(row);

  return (
    <div className={cn('pl-10', isEmpty && EMPTY_ADJUSTMENT_ROW_CLASS)}>
      <SalaryAdjustmentLineLabel
        type={
          isSalaryOnly ? SalaryAdjustmentLineTypes.Salary : SalaryAdjustmentLineTypes.Adjustment
        }
        isEmpty={isEmpty}
      />
    </div>
  );
};

/** Read-only, always-derived amount for the salary reconciliation line. */
export const SalaryAdjustmentTotal = ({ row }: SalaryCellProps) => {
  const adjustment = row.original as unknown as GenerateBillSalaryAdjustmentListItem;
  const { value, isEmpty } = useSalaryAdjustmentValue(row);

  return (
    <div className="flex justify-between">
      <span
        className={cn(
          'font-semibold',
          value < 0 && 'text-destructive',
          isEmpty && EMPTY_ADJUSTMENT_ROW_CLASS
        )}
      >
        {formatTrimmedDecimal(value, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}
      </span>
      <span className="text-muted-foreground">{adjustment.currency.code}</span>
    </div>
  );
};
